import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

/**
 * Building blocks for the customer-facing flow pages (`/embed/*`) in the
 * FleetSync look. Values are the `fleetsync` theme tokens
 * (packages/ui/src/tokens.css) used explicitly, because partner surfaces
 * render without `data-theme="fleetsync"`:
 *   teal #009080 · deep teal #00695e · ink #06131b · ground #f4f6f6
 *   mint #e6f4f2 · rule #e6e7e8 · soft text #5a616b
 */
export const FLOW_FONT_SANS: CSSProperties = {
  fontFamily:
    'var(--font-archivo), "Archivo", ui-sans-serif, system-ui, sans-serif',
};

export interface FlowPanelProps {
  /** Small mono uppercase label above the title (e.g. "Payment received"). */
  kicker?: string;
  /** Page heading, rendered as the page `h1`. */
  title: string;
  /** Optional icon shown above the kicker (e.g. the teal check on mint). */
  icon?: ReactNode;
  /** Optional one-line lead under the title. */
  description?: ReactNode;
  children?: ReactNode;
}

/** Page ground + centered column with the hero (kicker, title) and content. */
export function FlowPanel({
  kicker,
  title,
  icon,
  description,
  children,
}: FlowPanelProps) {
  return (
    <div
      className="min-h-screen bg-[#f4f6f6] py-8 text-[#06131b]"
      style={FLOW_FONT_SANS}
    >
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          {icon}
          {kicker && (
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-[#00695e]">
              {kicker}
            </span>
          )}
          <h1 className="text-balance text-3xl font-bold tracking-tight text-[#06131b] sm:text-4xl">
            {title}
          </h1>
          {description && (
            <p className="max-w-xl text-base text-[#5a616b]">{description}</p>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}

/** Flat white 12px card with a small mono title. */
export function FlowCard({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#e6e7e8] bg-white p-5">
      <h2 className="mb-3 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-[#5a616b]">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Label on the left, value on the right; `mono` renders data in Plex Mono. */
export function FlowRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-[#e6e7e8] py-2.5 text-sm first:border-t-0">
      <span className="text-[#5a616b]">{label}</span>
      <span
        className={`text-right font-medium text-[#06131b]${mono ? " font-mono" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

export const FLOW_BUTTON_CLASS =
  "inline-flex items-center justify-center gap-2 rounded-[10px] bg-[#009080] px-6 py-3 text-base font-semibold text-white transition-colors hover:bg-[#00695e] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#009080] disabled:cursor-not-allowed disabled:opacity-60";

/** The teal primary button of the flow pages. */
export function FlowButton({
  className,
  type = "button",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={
        className ? `${FLOW_BUTTON_CLASS} ${className}` : FLOW_BUTTON_CLASS
      }
      {...props}
    />
  );
}
